import { useCallback, useEffect, useState } from 'react';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import Modal from '../../../components/compliance/Modal';
import tprmService from '../../../services/compliance/tprm.service';
import thirdPartiesService from '../../../services/compliance/thirdParties.service';
import {
  ThirdParty,
  VendorImportResult,
  VendorImportRowError,
} from '../../../types/compliance';

const MAX_BYTES = 5 * 1024 * 1024;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fired when an upload succeeds. Receives the new draft vendor id. */
  onImported: (vendorId: string) => void;
  /** Surface notifications from the parent. */
  onError: (title: string, message: string) => void;
}

export default function VendorImportModal({ open, onClose, onImported, onError }: Props) {
  const [thirdParties, setThirdParties] = useState<ThirdParty[]>([]);
  const [thirdPartyId, setThirdPartyId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<VendorImportRowError[]>([]);
  const [warnings, setWarnings] = useState<VendorImportRowError[]>([]);

  useEffect(() => {
    if (!open) return;
    // Reset transient state each time the modal opens.
    setFile(null);
    setErrors([]);
    setWarnings([]);
    thirdPartiesService
      .list()
      .then((tps) => setThirdParties(tps))
      .catch((error: any) => {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
        onError('Failed to load third parties', msg);
      });
  }, [open, onError]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] || null;
      if (f && f.size > MAX_BYTES) {
        onError(
          'File too large',
          `Max file size is 5 MB. You selected ${(f.size / 1024 / 1024).toFixed(2)} MB.`,
        );
        setFile(null);
        e.target.value = '';
        return;
      }
      setFile(f);
    },
    [onError],
  );

  const handleUpload = useCallback(async () => {
    if (!file) {
      onError('No file selected', 'Choose an xlsx file first.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      onError('Invalid file', 'Only .xlsx files are supported.');
      return;
    }
    if (!thirdPartyId) {
      onError('Third Party required', 'Select the third party this vendor belongs to.');
      return;
    }
    setUploading(true);
    setErrors([]);
    setWarnings([]);
    try {
      const result: VendorImportResult = await tprmService.importVendor(file, thirdPartyId);
      if (result.warnings?.length) setWarnings(result.warnings);
      if (result.created_vendor_id) {
        onImported(result.created_vendor_id);
      } else {
        setErrors(result.errors || []);
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        setErrors(detail.errors as VendorImportRowError[]);
        if (Array.isArray(detail.warnings)) setWarnings(detail.warnings);
      } else {
        const msg = typeof detail === 'string' ? detail : error?.message || 'Unknown error occurred';
        onError('Upload failed', msg);
      }
    } finally {
      setUploading(false);
    }
  }, [file, thirdPartyId, onError, onImported]);

  // Alt+C closes. Import gets no chord — it ingests a whole vendor file. This
  // claim also wins over the shell's (we render <Modal>, so its effect runs first).
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import Vendor from Excel"
      description="Upload a completed vendor questionnaire to create a new Draft vendor record."
      sizeClass="max-w-xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-highlight disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading || !file || !thirdPartyId}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Upload & Import'}
          </button>
        </>
      }
    >
      <div>
        <label className="block text-xs font-medium text-label mb-1">
          Third Party <span className="text-red-600">*</span>
        </label>
        <select
          value={thirdPartyId}
          onChange={(e) => setThirdPartyId(e.target.value)}
          className="w-full mb-3 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
        >
          <option value="">— Select a third party —</option>
          {thirdParties.map((tp) => (
            <option key={tp.id} value={tp.id}>
              {tp.legal_entity_name || tp.name}
            </option>
          ))}
        </select>

        <label className="block text-xs font-medium text-label mb-1">
          Completed template (.xlsx) <span className="text-red-600">*</span>
        </label>
        <input
          type="file"
          accept=".xlsx"
          onChange={handleFileChange}
          className="block w-full text-sm mb-1"
        />
        <p className="text-xs text-gray-500">Only .xlsx files · Max 5 MB</p>

        {errors.length > 0 && (
          <div className="mt-4 p-3 border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950 rounded-lg">
            <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">
              Import errors ({errors.length})
            </h4>
            <ul className="text-xs text-red-700 dark:text-red-300 space-y-1 max-h-48 overflow-y-auto">
              {errors.map((err, i) => (
                <li key={`${err.row}-${err.field}-${i}`}>
                  <strong>Row {err.row}</strong>
                  {err.field ? ` · ${err.field}` : ''}: {err.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="mt-4 p-3 border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950 rounded-lg">
            <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2">
              Warnings ({warnings.length})
            </h4>
            <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1 max-h-48 overflow-y-auto">
              {warnings.map((w, i) => (
                <li key={`${w.row}-${w.field}-${i}`}>
                  <strong>Row {w.row}</strong>
                  {w.field ? ` · ${w.field}` : ''}: {w.message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
